import React from 'react';
import {
  PhoneArrowUpRightIcon,
  PhoneArrowDownLeftIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { formatDuracionLegible } from './callHistoryTime.util';

const ESTADO_CONFIG = {
  Contestada: {
    bg: 'bg-emerald-50 text-emerald-700 border-emerald-200/80',
    dot: 'bg-emerald-500',
  },
  'No contestó': {
    bg: 'bg-rose-50 text-rose-700 border-rose-200/80',
    dot: 'bg-rose-500',
  },
  Ocupado: {
    bg: 'bg-amber-50 text-amber-700 border-amber-200/80',
    dot: 'bg-amber-500',
  },
  Falló: {
    bg: 'bg-slate-100 text-slate-700 border-slate-300',
    dot: 'bg-slate-400',
  },
  Congestión: {
    bg: 'bg-purple-50 text-purple-700 border-purple-200',
    dot: 'bg-purple-500',
  },
};

export function CallHistoryTable({ data, loading, onSelectClient, onSelectPhone, cambiarPeriodo, canCall = false }) {
  if (loading && (!data || data.length === 0)) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-14 text-center shadow-xs">
        <div className="w-9 h-9 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm font-bold text-slate-800">Cargando registros de llamadas...</p>
        <p className="text-xs text-slate-400 mt-1">Consultando la base de datos de telefonía CDR_Issabel</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-xs">
        <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-3 text-xl">
          📞
        </div>
        <p className="text-sm font-bold text-slate-800">No se encontraron llamadas en este período</p>
        <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
          Prueba cambiando los filtros de período rápido o seleccionando un rango de fechas diferente.
        </p>
        {cambiarPeriodo && (
          <div className="mt-4 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => cambiarPeriodo('semana')}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 transition cursor-pointer"
            >
              📅 Ver Últimos 7 días
            </button>
            <button
              type="button"
              onClick={() => cambiarPeriodo('mes')}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300 transition cursor-pointer"
            >
              🗓️ Ver Este mes
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200/90 rounded-xl overflow-hidden shadow-xs">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50/90 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <th className="py-3 px-3.5 whitespace-nowrap">Fecha</th>
              <th className="py-3 px-2.5 whitespace-nowrap">Hora</th>
              <th className="py-3 px-3 whitespace-nowrap">Gestor</th>
              <th className="py-3 px-2 text-center whitespace-nowrap">Ext.</th>
              <th className="py-3 px-3 whitespace-nowrap">Dirección</th>
              <th className="py-3 px-3 whitespace-nowrap">Teléfono</th>
              <th className="py-3 px-3.5 min-w-[210px]">Cliente & Compra #ID</th>
              <th className="py-3 px-3 text-center whitespace-nowrap">Estado</th>
              <th className="py-3 px-3 text-right whitespace-nowrap">Duración</th>
              <th className="py-3 px-3.5 text-right whitespace-nowrap">Hablado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
            {data.map((call, idx) => {
              const isSaliente = call.Direccion === 'Saliente';
              const estado = ESTADO_CONFIG[call.Estado] || {
                bg: 'bg-slate-100 text-slate-700 border-slate-200',
                dot: 'bg-slate-400',
              };
              const isCallAnswered = call.Estado === 'Contestada';
              const uniqueKey = call.uniqueid || `${call.Fecha}-${call.Hora}-${call.Telefono}-${idx}`;

              return (
                <tr
                  key={uniqueKey}
                  className="hover:bg-slate-50/80 transition-colors group"
                >
                  {/* 1. Fecha */}
                  <td className="py-2.5 px-3.5 font-semibold text-slate-900 whitespace-nowrap font-mono text-[11.5px]">
                    {call.Fecha}
                  </td>

                  {/* 2. Hora */}
                  <td className="py-2.5 px-2.5 text-slate-500 font-mono whitespace-nowrap text-[11px]">
                    {call.Hora}
                  </td>

                  {/* 3. Gestor */}
                  <td
                    className="py-2.5 px-3 font-semibold text-slate-800 whitespace-nowrap max-w-[150px] truncate"
                    title={call.Gestor}
                  >
                    {call.Gestor}
                  </td>

                  {/* 4. Extensión */}
                  <td className="py-2.5 px-2 text-center font-mono font-bold text-slate-600 whitespace-nowrap text-[11px]">
                    {call.Extension || '-'}
                  </td>

                  {/* 5. Dirección */}
                  <td className="py-2.5 px-3 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                        isSaliente
                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                          : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      }`}
                    >
                      {isSaliente ? (
                        <>
                          <PhoneArrowUpRightIcon className="w-3 h-3 text-blue-600" />
                          Saliente
                        </>
                      ) : (
                        <>
                          <PhoneArrowDownLeftIcon className="w-3 h-3 text-emerald-600" />
                          Entrante
                        </>
                      )}
                    </span>
                  </td>

                  {/* 6. Teléfono con Marcación Rápida solo si canCall es true */}
                  <td className="py-2.5 px-3 font-mono font-bold whitespace-nowrap">
                    {canCall && onSelectPhone ? (
                      <button
                        type="button"
                        onClick={() =>
                          onSelectPhone({
                            telefono: call.Telefono,
                            cliente: call.Cliente,
                            idCompra: call.idCompra,
                          })
                        }
                        className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer group/btn"
                        title="Hacer clic para cargar en el marcador del celular con su cliente y compra"
                      >
                        <span className="text-[12px] font-bold text-slate-900 group-hover/btn:text-indigo-600">
                          {call.Telefono}
                        </span>
                        <span className="w-5 h-5 rounded-md bg-indigo-50 border border-indigo-200/80 text-indigo-600 group-hover/btn:bg-indigo-600 group-hover/btn:text-white transition flex items-center justify-center">
                          <PhoneIcon className="w-3 h-3" />
                        </span>
                      </button>
                    ) : (
                      <span
                        className="text-[12px] font-bold text-slate-800 select-all"
                        title="Modo consulta: llamadas deshabilitadas fuera del detalle de cobranza"
                      >
                        {call.Telefono}
                      </span>
                    )}
                  </td>

                  {/* 7. Cliente & Compra #ID (SIN CLP NI MONEDAS ALUCINADAS) */}
                  <td className="py-2.5 px-3.5">
                    <div className="flex flex-col">
                      <span
                        className="font-bold text-slate-900 truncate max-w-[240px]"
                        title={call.Cliente}
                      >
                        {call.Cliente || 'Cliente no asignado'}
                      </span>
                      {call.idCompra && (
                        <span className="text-[11px] text-indigo-600 font-mono font-bold tracking-tight">
                          Compra #{call.idCompra}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* 8. Estado con Indicador */}
                  <td className="py-2.5 px-3 text-center whitespace-nowrap">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${estado.bg}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${estado.dot}`} />
                      {call.Estado}
                    </span>
                  </td>

                  {/* 9. Duración Total */}
                  <td className="py-2.5 px-3 text-right font-mono text-slate-600 whitespace-nowrap">
                    <span className="font-semibold text-slate-700">
                      {formatDuracionLegible(call.DuracionTotalSeg, call.DuracionTotalMin)}
                    </span>
                  </td>

                  {/* 10. Tiempo Hablado */}
                  <td className="py-2.5 px-3.5 text-right font-mono whitespace-nowrap">
                    <span
                      className={`font-black ${
                        isCallAnswered
                          ? 'text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/80'
                          : 'text-slate-400'
                      }`}
                    >
                      {formatDuracionLegible(call.TiempoHabladoSeg, call.TiempoHabladoMin)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
